/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import IMask from 'imask';
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  DialogIcon,
  ViewText,
  RadioContainer,
  CalendarContainer,
  ButtonContainer,
  ButtonContent,
  LabelEdit
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import CheckBox from "components/Form/CheckBox";
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import { RadioButton } from "components/Form/Radio/index";
import {DayPicker} from 'react-day-picker';
import SearchField from 'components/Form/SearchField';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import MasksHelper from 'utils/src/masks';
import moment from 'moment';

const GovernmentStatesInformation = ({ idPerson, columns, data, hasPermission, onSavePersonRelationship, feeTypeOptions, taxTypeOptions, rateBasisOptions, chargeTypeOptions, periodicityOptions }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [relationshipContent, setRelationshipContent] = useState([]);
  const [indexAccount, setIndexAccount] = useState(null);
  const maskHelper = new MasksHelper();
  const [showCalendar, setShowCalendar] = useState('');
  const [isPercentage, setIsPercentage] = useState(false);
  const [periodsCalendar, setPeriodsCalendar] = useState([]);

  let defaultTaxType;
  let defaultRateBasis;

  taxTypeOptions.map(taxType => {
    if (taxType.value.idDomain === 659) {
      defaultTaxType = taxType.value;
    }
  });

  rateBasisOptions.map(rateBasis => {
    if (rateBasis.value.idDomain === 665) {
      defaultRateBasis = rateBasis.value;
    }
  });

  const generatePeriods = (values) => {
    // define intervals
    const periodicity = {
      631: { frequency: 12, interval: 1 },
      634: { frequency: 3, interval: 4 },
      632: { frequency: 6, interval: 2 },
      633: { frequency: 4, interval: 3 },
      635: { frequency: 1, interval: 12 },
    };

    let startDate = moment();
    let [day, month] = values.collection_period.start.split('/');
    let year = startDate.get('year');
    let [dayEnd] = values.collection_period.end.split('/');

    //Months: 0-11
    startDate.month((month - 1)).date(day);

    let tablePeriods = [];
    for (let index = 0; index < periodicity[values.frequency.idDomain].interval; index++) {
      let element = {};

      let periodStart = moment([year, (month - 1), day]);
      index === 0
        ? element.date_start = startDate.format('DD/MM')
        : element.date_start = periodStart.add((Number(periodicity[values.frequency.idDomain].frequency)) * (index), 'M').format('DD/MM');

      let periodEnd = periodStart.clone();
      periodEnd.add(Number(periodicity[values.frequency.idDomain].frequency) - 1, 'M');

      periodEnd.daysInMonth() < 31 && Number(dayEnd) === 31
        ? periodEnd.endOf('month')
        : (day > dayEnd)
          ? periodEnd.add(1, 'M').date(dayEnd)
          : periodEnd.date(dayEnd);

      element.date_end = periodEnd.format('DD/MM');

      periodEnd.add(Number(values.collection_due_month), 'M')
      periodEnd.daysInMonth() < 31 && Number(values.collection_due_day) === 31
        ? periodEnd.endOf('month')
        : periodEnd.date(values.collection_due_day);

      element.due_date = periodEnd.format('DD/MM');
      tablePeriods.push(element);
    }

    setPeriodsCalendar(tablePeriods);
    formikRef.current?.setFieldValue('periods', tablePeriods);
  };

  const [initial, setInitial] = useState({
    feeType: '',
    taxType: defaultTaxType,
    rateBasis: defaultRateBasis,
    chargeType: '',
    feeValue: '',
    chargeTypeDomain: '',
    collection_period: {
      start: '01/01',
      end: '31/12',
    },
    frequency: '',
    collection_due_month: '',
    collection_due_day: '',
    note: '',
    periods: [],
  });

  const setEditPersonRelationship = (personRelationship, indexAccount) => {
    formikRef.current.values.feeType = feeTypeOptions.find(v => v.value.idDomain === personRelationship.relationshipContent.feeTypeDomain)?.value;
    formikRef.current.values.taxType = taxTypeOptions.find(v => v.value.idDomain === personRelationship.relationshipContent.taxTypeDomain)?.value;
    formikRef.current.values.rateBasis = rateBasisOptions.find(v => v.value.idDomain === personRelationship.relationshipContent.rateBasisDomain)?.value;
    formikRef.current.values.chargeType = chargeTypeOptions.find(v => v.value.idDomain === personRelationship.relationshipContent.chargeTypeDomain)?.value;
    formikRef.current.values.feeValue = String(personRelationship.relationshipContent.feeValue);
    formikRef.current.values.chargeTypeDomain = personRelationship.relationshipContent.chargeTypeDomain;
    formikRef.current.values.collection_period = personRelationship.relationshipContent.collection_period;
    formikRef.current.values.frequency = periodicityOptions.find(v => v.value.idDomain === personRelationship.relationshipContent.frequencyDomain)?.value;
    formikRef.current.values.collection_due_month = String(personRelationship.relationshipContent.collection_due_month || '');
    formikRef.current.values.collection_due_day = String(personRelationship.relationshipContent.collection_due_day || '');
    formikRef.current.values.note = personRelationship.relationshipContent.note;
    formikRef.current.values.periods = personRelationship.relationshipContent.periods;
    setPeriodsCalendar(personRelationship.relationshipContent.periods);

    if (formikRef.current.values.chargeType?.idDomain === 629) formikRef.current.values.feeValue = formikRef.current.values.feeValue * 100;

    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100);
    setTimeout(() => inputRef.current && inputRef.current.blur(), 200);
  }

  function applyMaskPercentage(value) {
    let maskPercentage = maskHelper.getPercentage(authContext.region, 5, String(100), '0', true);
    let mask = IMask.createMask(maskPercentage);
    mask.resolve(`${value}`);
    return mask.value;
  }

  function applyMaskCurrency(value) {
    let maskCurrency = maskHelper.getMask("currency", authContext.region, 2, null, null, 0);
    let mask = IMask.createMask(maskCurrency);
    mask.resolve(`${value}`);
    return mask.value;
  }

  const handleGetPeriodsCalendar = () => {
    if (!periodsCalendar || periodsCalendar.length < 1) return;
    return <Table>
      <TableHead>
        <TableRow>
          <TableHeader>
            <GridColumnTitle style={{ cursor: 'not-allowed' }}>
              PERIOD START
            </GridColumnTitle>
          </TableHeader>
          <TableHeader>
            <GridColumnTitle style={{ cursor: 'not-allowed' }}>
              PERIOD END
            </GridColumnTitle>
          </TableHeader>
          <TableHeader>
            <GridColumnTitle style={{ cursor: 'not-allowed' }}>
              DUE DATE
            </GridColumnTitle>
          </TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        <FieldArray
          name='periods'
        >
          <>
            {periodsCalendar.map((element, index) =>
              <TableRow key={index}>
                <TableCollum>
                  <InlineLabel>
                    PERIOD START
                  </InlineLabel>
                  <LabelEdit fontSize={12}>
                    {element.date_start}
                  </LabelEdit>
                </TableCollum>

                <TableCollum>
                  <InlineLabel>
                    PERIOD END
                  </InlineLabel>
                  <LabelEdit fontSize={12}>
                    {element.date_end}
                  </LabelEdit>
                </TableCollum>

                <TableCollum>
                  <Field
                    name={`periods.${index}.due_date`}
                    //label="Fee Value"
                    component={InputField}
                    value={element.due_date}
                    readOnly
                    rightIcon={'calendar'}
                    rightIconClick={() => { showCalendar === `periods.${index}.due_date` ? setShowCalendar("") : setShowCalendar(`periods.${index}.due_date`) }}
                  />
                  {showCalendar && showCalendar === `periods.${index}.due_date` &&
                    <CalendarContainer>
                      <DayPicker
                        mode="single"
                        style
                        firstDayOfWeek={0}
                        fixedWeeks
                        onDayClick={(date, { disabled }) => {
                          if (disabled) return;
                          let momentDate = moment(new Date(date));
                          if (momentDate.isValid()) {
                            formikRef.current?.setFieldValue(`periods.${index}.due_date`, momentDate.format('DD/MM'));
                          };
                          setShowCalendar("");
                        }}
                      ></DayPicker>
                    </CalendarContainer>}
                </TableCollum>
              </TableRow>
            )}
          </>
        </FieldArray>
      </TableBody>
    </Table>
  };

  const resetForm = () => {
    formikRef.current.values.feeType = '';
    formikRef.current.values.taxType = defaultTaxType;
    formikRef.current.values.rateBasis = defaultRateBasis;
    formikRef.current.values.chargeType = '';
    formikRef.current.values.feeValue = '';
    formikRef.current.values.chargeTypeDomain = '';
    formikRef.current.values.collection_period = {
      start: '01/01',
      end: '31/12',
    };
    formikRef.current.values.frequency = '';
    formikRef.current.values.collection_due_month = '';
    formikRef.current.values.collection_due_day = '';
    formikRef.current.values.note = '';
    formikRef.current.values.periods = [];
    setPeriodsCalendar([]);
  }

  function removeMask(value) {
    if (typeof value === 'number' || !value) return value;

    var maskPercentage = {
      mask: Number,
      scale: 5,
      signed: true,
      thousandsSeparator: ',',
      padFractionalZeros: true,
      normalizeZeros: false,
      radix: '.',
      min: 0,
    };

    let maskCurrency = IMask.createMask(maskPercentage);
    maskCurrency.resolve(value);
    var valueUnformated = maskCurrency.unmaskedValue;
    return parseFloat(valueUnformated);
  }

  const saveRelationship = async body => {
    console.log("BODY save relationship", body);
    try {
      loading.start();
      body.idPersonHead = idPerson;
      body.idPersonTail = authContext?.login_response?.ret_value?.idPersonRoot;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 616;
      body.isGovernmentTaxes = true;
      body.relationshipContent = {
        feeType: body.feeType.description ? body.feeType.description : body.feeType,
        taxType: body.taxType.description ? body.taxType.description : body.taxType,
        rateBasis: body.rateBasis.description ? body.rateBasis.description : body.rateBasis,
        chargeType: body.chargeType.description ? body.chargeType.description : body.chargeType,
        feeValue: body.chargeType.idDomain === 629 ? removeMask(body.feeValue) / 100 : body.feeValue,
        collection_period: body.collection_period,
        frequency: body.frequency.description ? body.frequency.description : body.frequency,
        collection_due_month: body.collection_due_month,
        collection_due_day: body.collection_due_day,
        note: body.note,
        periods: body.periods,
        feeTypeDomain: body.feeType.idDomain,
        taxTypeDomain: body.taxType.idDomain,
        rateBasisDomain: body.rateBasis.idDomain,
        chargeTypeDomain: body.chargeType.idDomain,
        frequencyDomain: body.frequency.idDomain
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdPersonRelationship(null);
      setAddPersonRelationship(false);
      onSavePersonRelationship && onSavePersonRelationship();
      resetForm();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const deletePersonRelationship = async idPersonRelationship => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);
      setIndexAccount(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIndexAccount(null);
      setIdPersonRelationship(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }


  const createValidation = () => {
    let validations = yup.object().shape({
      feeType: yup.string().required("* Required field"),
      taxType: yup.string().required("* Required field"),
      rateBasis: yup.string().required("* Required field"),
      chargeType: yup.string().required("* Required field"),
      collection_period: yup.object().shape({
        start: yup.string().required("* Required field"),
        end: yup.string().required("* Required field"),
      }),
      frequency: yup.string().required("* Required field"),
      collection_due_month: yup.string().required("* Required field"),
      collection_due_day: yup.string().required("* Required field"),
      feeValue: yup.string().required("* Required field"),
    });

    setValidations(validations);
    return validations;
  }

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(JSON.parse(JSON.stringify(values)))}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); setIndexAccount(null); }} onConfirm={() => deletePersonRelationship(idPersonRelationship)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() => data.map((item, index) => (
                  <TableRow key={index} >
                    <TableCollum>
                      <InlineLabel>
                        Fee Type
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.feeType}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Tax Type
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.taxType}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Rate & Basis
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.rateBasis}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Charge Type
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.chargeType}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Fee Value
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.chargeTypeDomain === 629 ? applyMaskPercentage(String(item.relationshipContent.feeValue * 100)) : applyMaskCurrency(String(item.relationshipContent.feeValue))}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum>
                      <InlineLabel>
                        Notes
                      </InlineLabel>
                      <LabelEdit fontSize={12}>
                        {item.relationshipContent.note || '-'}
                      </LabelEdit>
                    </TableCollum>

                    <TableCollum right>
                      <InlineLabel>
                        ACTION
                      </InlineLabel>
                      <Label>
                        <Icon
                          name="edit"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => setEditPersonRelationship(item)}
                        />
                        <Icon
                          name="trash"
                          style={{ alignSelf: "center", cursor: "pointer" }}
                          onClick={() => {
                            setIdPersonRelationship(item.idPersonRelationship);
                            setDeleteModalOpen(true);
                            setAddPersonRelationship(false);
                            resetForm();
                          }} />
                      </Label>
                    </TableCollum>
                  </TableRow>
                ))
                }
              />
            </TableBody>
          </Table>
          {!addPersonRelationship && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddPersonRelationship(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}

          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="feeType"
                    label="Fee Type"
                    isRequired={true}
                    options={feeTypeOptions || []}
                    searchList={feeTypeOptions || []}
                    onChange={(e) => { setFieldValue(`feeType`, e); }}
                    component={ComboBox}
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="taxType"
                    label="Tax Type"
                    isRequired={true}
                    options={taxTypeOptions || []}
                    searchList={taxTypeOptions || []}
                    onChange={(e) => { setFieldValue(`taxType`, e); }}
                    component={ComboBox}
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="rateBasis"
                    label="Rate & Basis"
                    isRequired={true}
                    options={rateBasisOptions || []}
                    searchList={rateBasisOptions || []}
                    onChange={(e) => { setFieldValue(`rateBasis`, e); }}
                    component={ComboBox}
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="chargeType"
                    label="Charge Type"
                    isRequired={true}
                    options={chargeTypeOptions || []}
                    searchList={chargeTypeOptions || []}
                    component={ComboBox}
                    onChange={(e) => {
                      setFieldValue(`chargeType`, e);
                      setFieldValue('feeValue', String(0))
                      setFieldValue(`chargeTypeDomain`, e.idDomain);
                      if (e == 'Percent') {
                        setIsPercentage(true)
                      } else {
                        setIsPercentage(false)
                      }
                    }}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="feeValue"
                    label="Fee Value"
                    component={InputField}
                    onInputRef={(ref) => inputRef.current = ref}
                    mask={values.chargeTypeDomain == 630 ? maskHelper.getMask("currency", authContext.region, 2, null, null, 0) : maskHelper.getPercentage(authContext.region, 5, String(100), '0')}
                    isRequired
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="collection_period.start"
                    label="Competence Start"
                    component={InputField}
                    isRequired
                    rightIcon={'calendar'}
                    rightIconClick={() => { showCalendar === "collection_period.start" ? setShowCalendar("") : setShowCalendar("collection_period.start") }}
                  />
                  {showCalendar && showCalendar === "collection_period.start" &&
                    <CalendarContainer>
                      <DayPicker
                        mode="single"
                        style
                        firstDayOfWeek={0}
                        fixedWeeks
                        onDayClick={(date, { disabled }) => {
                          if (disabled) return;
                          let formattedDate = "";
                          let selectedEndDate = "";
                          let momentDate = moment(new Date(date));
                          if (momentDate.isValid()) {
                            formattedDate = momentDate.format('DD/MM');
                            selectedEndDate = momentDate.add(1, 'y').subtract(1, 'days').format('DD/MM');
                            setFieldValue("collection_period.start", formattedDate);
                            setFieldValue("collection_period.end", selectedEndDate);
                          };
                          setShowCalendar("");
                        }}
                      ></DayPicker>
                    </CalendarContainer>
                  }
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="collection_period.end"
                    label="Competence End"
                    component={InputField}
                    rightIcon={'calendar'}
                    rightIconClick={() => { showCalendar === "collection_period.end" ? setShowCalendar("") : setShowCalendar("collection_period.end") }}
                    disabled
                    isRequired
                  />
                  {showCalendar && showCalendar === "collection_period.end" &&
                    <CalendarContainer>
                      <DayPicker
                        mode="single"
                        style
                        firstDayOfWeek={0}
                        fixedWeeks
                        onDayClick={(date, { disabled }) => {
                          if (disabled) return;
                          let formattedDate = "";
                          let selectedDate = "";
                          let momentDate = moment(new Date(date));
                          if (momentDate.isValid()) {
                            formattedDate = momentDate.format('DD/MM');
                            selectedDate = momentDate.format("YYYY-MM-DD");
                            setFieldValue("collection_period.end", formattedDate);
                          };
                          setShowCalendar("");
                        }}
                      ></DayPicker>
                    </CalendarContainer>
                  }
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="frequency"
                    label="Calculation Periodicity"
                    component={ComboBox}
                    options={periodicityOptions || []}
                    searchList={periodicityOptions || []}
                    isRequired
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="collection_due_month"
                    label="Collection Deadline (month)"
                    component={InputField}
                    mask={{
                      "type": "currency",
                      "typeAux": "integer",
                      "mask": 0,
                      "scale": 0,
                      "signed": false,
                      "normalizeZeros": false,
                      "padFractionalZeros": false,
                      "thousandsSeparator": "",
                      "max": 12,
                      "min": 1
                    }}
                    isRequired
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Field
                    name="collection_due_day"
                    label="Payment day of month"
                    mask={{
                      "type": "currency",
                      "typeAux": "integer",
                      "mask": 0,
                      "scale": 0,
                      "signed": false,
                      "normalizeZeros": false,
                      "padFractionalZeros": false,
                      "thousandsSeparator": "",
                      "max": 31,
                      "min": 1
                    }}
                    isRequired
                    component={InputField}
                  />
                </Col>
                <Col lg={3} md={3} sm={3} xs={12}>
                  <Button
                    fullWidth
                    primary
                    style={{ marginTop: "20px" }}
                    onClick={() => {
                      createValidation().validate(values).then(() => {
                        generatePeriods(values);
                      }).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                        return;
                      })
                    }
                    }
                  >
                    GENERATE CALENDAR
                  </Button>
                </Col>

              </Row>

              <Row>
                <Col lg={9} md={9} sm={9} xs={12}>
                  <Field
                    name="note"
                    label="Note"
                    component={InputField}
                  />
                </Col>
              </Row>

              <Row>
                {periodsCalendar && handleGetPeriodsCalendar()}
              </Row>


              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setIdPersonRelationship(null);
                      setAddPersonRelationship(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )
      }
    </Formik >
  )
}

export default GovernmentStatesInformation;